import { useState } from 'react';
import { Button } from '@centraoneplus/ui';
import { ThemedSelect } from '@components/common/ThemedSelect';
import type { TeamWithMembers } from '../../../types/team';
import { attachCandidates, type TeamViewer } from './teamHierarchy';
import { MoveTeamConfirmation } from './MoveTeamConfirmation';

interface AttachExistingTeamDialogProps {
  /** The team the chosen team goes under. */
  parent: TeamWithMembers;
  /** Every team in the tenant, to pick from. */
  teams: TeamWithMembers[];
  viewer: TeamViewer;
  onAttach: (teamId: string, moveParentAgents: boolean) => void;
  onCancel: () => void;
  isSaving: boolean;
}

/**
 * "Add existing team": pick a team to put under `parent`, then confirm what
 * that changes. Admins can also take a sub-team from another parent; anyone
 * else (the parent's head) only sees standalone teams.
 */
export function AttachExistingTeamDialog({
  parent,
  teams,
  viewer,
  onAttach,
  onCancel,
  isSaving,
}: AttachExistingTeamDialogProps) {
  const candidates = attachCandidates(teams, parent, viewer);
  const [teamId, setTeamId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const chosen = candidates.find((t) => t.id === teamId);

  if (confirming && chosen) {
    return (
      <MoveTeamConfirmation
        teamId={chosen.id}
        teamName={chosen.name}
        target={{ id: parent.id, name: parent.name }}
        onConfirm={(moveParentAgents) => onAttach(chosen.id, moveParentAgents)}
        onCancel={() => setConfirming(false)}
        isSaving={isSaving}
      />
    );
  }

  return (
    <div className="space-y-4">
      {candidates.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="no-attach-candidates">
          There are no teams that can go under {parent.name}. A team with sub-teams of its own cannot
          become a sub-team{viewer.isAdmin ? '' : ', and only an admin can move a sub-team from another parent'}.
        </p>
      ) : (
        <div>
          <label
            htmlFor="attach-team-picker"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Team to add under {parent.name}
          </label>
          <ThemedSelect
            id="attach-team-picker"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">Select a team</option>
            {candidates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.parentName ? ` (currently under ${t.parentName})` : ''}
              </option>
            ))}
          </ThemedSelect>
        </div>
      )}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={() => setConfirming(true)} disabled={!chosen || isSaving}>
          Continue
        </Button>
      </div>
    </div>
  );
}
